import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import {
  selectDialogState,
  selectIsLoadingDevelopmentStageData,
  selectVehicleProgramsSelectedDevelopmentStageData,
  setIsConfigSensorReferenceFrameDialogOpen,
  setVehicleProgramsSelectedDevelopmentStageData
} from "../../../../redux/ducks/VehicleProgramsDetails/slice";
import * as React from "react";
import {useEffect, useState} from "react";
import {ReferenceFrame, updateSensorPositionsEndpoint, vehicleDimensionsKeyOrder, vehicleMeasurementsKeys, VehicleMeasurementsRow} from "./constants";
import {stringToDateToLocaleString} from "../../../../helpers/helpers";
import cloneDeep from "lodash/cloneDeep";
import {MRT_ColumnDef} from "material-react-table";
import {usePostRequest} from "../../../../api/helper";
import styled from "styled-components";
import Loader from "../../../components/Loader/Loader";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import ArgosCollapseComponent from "../../../components/CollapseComponent/ArgosCollapseComponent";
import {StyledArgosDialog} from "../../../components/Dialogs/ArgosDialog";
import {ConfigSensorReferenceFrameDialog} from "../../../components/Dialogs/VehicleProgramsDetailsDialog/ConfigSensorReferenceFrameDialog";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


const VehicleMeasurements = (props: any) => {
  const dispatch = useAppDispatch()
  const {developmentStageId, vehicleProgramId} = useGetUrlParams()

  /**
   * Development stage data
   */
  const loadingDevelopmentStageData = useAppSelector(selectIsLoadingDevelopmentStageData)
  const developmentStageData = useAppSelector(selectVehicleProgramsSelectedDevelopmentStageData)

  const configSensorReferenceFrameOpen = useAppSelector(state => selectDialogState(state).isConfigSensorReferenceFrameDialogOpen)

  const [globalReferenceFrame, setGlobalReferenceFrame] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })
  const [localReferenceFrame, setLocalReferenceFrame] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })
  const [originTransformation, setOriginTransformation] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })

  /**
   * Tracking vehicle
   */
  const [trackingVehicle, setTrackingVehicle] = React.useState<string>("")
  const [designerName, setDesignerName] = React.useState<string>("")
  const [dateEntered, setDateEntered] = React.useState<string>("")  
  const [dateCollected, setDateCollected] = React.useState<string>("")
  useEffect(() => {
    setTrackingVehicle(developmentStageData?.vehicle_measurements?.tracking_vehicle)
    setDateCollected(stringToDateToLocaleString(developmentStageData?.vehicle_measurements?.vehicle_program_development_stage, false, false))
    setDateEntered(stringToDateToLocaleString(developmentStageData?.vehicle_measurements?.date, false, false))
    setDesignerName(developmentStageData?.vehicle_measurements?.designer_name)
  }, [developmentStageData])

  /**
   * Collapsible component data
   */
  useEffect(() => {
    const clonedDevelopmentStageData = cloneDeep(developmentStageData)
    const map = createCollapsibleDataMap(clonedDevelopmentStageData?.vehicle_measurements?.dimensionsListTableData)
    setCollapsibleDataMap(map)
  }, [developmentStageData]);
  const [collapsibleDataMap, setCollapsibleDataMap] = React.useState<any>({})
  /**
   * Collapsible data map: header of the list and values are the components of the sublist.  
   *     
   * @param data is a map where the keys are the values to be displayed
   * @returns a map with componentType, data, dialogState props.                              
   */
  const createCollapsibleDataMap = (data: any) => {
    const collapsibleDataMap: any = {}
    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        Object.keys(item).forEach((key) => {
          const keyDisplay=vehicleMeasurementsKeys[key as keyof typeof vehicleMeasurementsKeys]
          collapsibleDataMap[keyDisplay] = createVehicleMeasurementsTab(item[key])
        });
      });
    }
    return collapsibleDataMap
  }

  /**
   * Measurements data
   * @param versionObject
   */
  const mapVehicleMeasurementsData = (versionObject: any) => {
    const tempData = versionObject?.vehicle_measurements?.dimensions_list
    if (!tempData) return []
    const sortedTempData = tempData?.sort((dimensionPropertyOne: any, dimensionPropertyTwo: any) => {
      return vehicleDimensionsKeyOrder.indexOf(dimensionPropertyOne?.property_name) - vehicleDimensionsKeyOrder.indexOf(dimensionPropertyTwo?.property_name)
    });
    return sortedTempData
  }
  const vehicleMeasurementsColumns: MRT_ColumnDef<VehicleMeasurementsRow>[] = [
    {
      header: "Property",
      accessorKey: "property_name",
      enableEditing: false,
      minSize: 80,
      size: 280,

    },
    {
      id: 'value',
      header: "Value",
      accessorKey: "value",
      enableEditing: false,
      minSize: 80,
      size: 150,

    },
    {
      id: 'units',
      header: "Units",
      accessorKey: "units",
      enableEditing: false,
      minSize: 80,
      size: 150,

    },
    {
      id: 'details',
      header: "Details",
      accessorKey: "details",
      enableEditing: false,
      minSize: 80,
      size: 500,

    },
  ]
  const createVehicleMeasurementsTab = (data: any) => {
    return (
      <ArgosFlexbox style={{overflow: "auto"}} vertical>
        <ArgosTableSimple
          columns={vehicleMeasurementsColumns}
          data={data ?? []}
        />
      </ArgosFlexbox>
    )
  }

  const updateSensorPositionsHandle = usePostRequest(updateSensorPositionsEndpoint)
  const updateSensorPositions = (reqBody: any) => {
    updateSensorPositionsHandle(reqBody).then((data: any) => {
      const _developmentStageData = cloneDeep(developmentStageData)
      _developmentStageData['sensor_positions_data'] = data['sensors']
      _developmentStageData['sensor_positions_table_data'] = data['table_data']
      dispatch(setVehicleProgramsSelectedDevelopmentStageData(_developmentStageData))
    })
  }

  const StyledDiv = styled.div({
    padding: 10,
    fontSize: 15
  })

  return (
    <div style={{height: '100%', width: '100%', overflow: 'auto'}}>
      <Loader isLoading={loadingDevelopmentStageData}>
        <ArgosFlexbox vertical style={{overflow: 'auto'}}>
          <ArgosFlexbox justifyContent="right">
            <StyledDiv color="GrayText">Tracking Vehicle: {trackingVehicle}</StyledDiv>
            <StyledDiv color="GrayText">Request Date: {dateEntered}</StyledDiv>
            <StyledDiv color="GrayText">Data Collection Date: {dateCollected}</StyledDiv>
          </ArgosFlexbox>
          <div style={{overflow: 'auto', overflowX: 'hidden', flex: 1}}>
            <ArgosCollapseComponent
              data={collapsibleDataMap}
            />
          </div>
        </ArgosFlexbox>
      </Loader>

      <StyledArgosDialog
        width={1200}
        height={620}
        open={configSensorReferenceFrameOpen}
        onClose={() => {
          dispatch(setIsConfigSensorReferenceFrameDialogOpen(false))
        }}
      >
        <ConfigSensorReferenceFrameDialog
          vehicleProgramId={vehicleProgramId}
          developmentStageId={developmentStageId}
          globalReferenceFrame={globalReferenceFrame}
          setGlobalReferenceFrame={setGlobalReferenceFrame}
          localReferenceFrame={localReferenceFrame}
          setLocalReferenceFrame={setLocalReferenceFrame}
          originTransformation={originTransformation}
          setOriginTransformation={setOriginTransformation}
          updateSensorPositions={updateSensorPositions}
        />
      </StyledArgosDialog>
    </div>
  )
}


export default VehicleMeasurements
